import handleCopy from '@context/utils/linked/handleCopy'
import { getProxyUrl } from '@proxies/index'
import { useAppStore } from '@store'
import {
  AnyID,
  LinkedItem,
  LinkedItemSon,
  ReactMouseEvent,
  SceneObjects,
  SetState,
  Textures,
} from '@types'
import { BackAcabadosInfo } from '@utils/conversion/getStandardMaterial'
import { handleConcatStrings } from '@utils/handleConcatStrings'
import {
  MutableRefObject,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import handleApplyToAllItems from './utils/linked/handleApplyToAllItems'
import handleGroupClick from './utils/linked/handleGroupClick'
import { handlePaste } from './utils/linked/handlePaste'
import HandlePasteAll from './utils/linked/handlePasteAll'
import handleTextureClick from './utils/linked/handleTextureClick'
import { handleZoneClick } from './utils/linked/handleZoneClick'
import { updateRightInfo } from './utils/linked/updateRightInfo'
import { PasteRecord } from './utils/shared'
import fetchAcabadoZonaModeloAcabadoInfo from '@utils/sceneObjects/linked/fetchAcabadoZonaModeloAcabadoInfo'
import fetchTexturas from '@utils/sceneObjects/linked/fetchTexturas'
// import { current } from 'immer'

type CenterInfo = {
  idAcabadoUnidadModeloAcabado: number
  acabado: string
  modelo?: string
  zona?: string
  texturas?: any
}[]

type RightInfo = {
  name: string
  textures: BackAcabadosInfo[]
}

type Options = Record<
  number,
  Record<
    number,
    {
      idElementosEnlazados: number
      idUnidadENlazada: number
      descripcion: string
      referenciaJpgpng: string
      ordenVisual: number
      porDefecto: boolean
    }
  >
>

export type LinkedContextState = {
  currId: MutableRefObject<string>
  object: MutableRefObject<LinkedItem>
  copiedIconId: string
  setCopiedIconId: SetState<string>
  isModel: boolean
  setIsModel: SetState<boolean>
  highlighted: string
  setHighlighted: SetState<string>
  modelo: string
  setModelo: SetState<string>
  acabado: string
  setAcabado: SetState<string>
  centerInfo: CenterInfo
  setCenterInfo: SetState<CenterInfo>
  rightInfo: RightInfo
  setRightInfo: SetState<RightInfo>
  copiedTexture: Textures[number] | null
  setCopiedTexture: SetState<NonNullable<Textures[number]>>
  linkedOptions: Options | null
  childrenObject: MutableRefObject<LinkedItemSon | undefined>
  selectedTexture: (BackAcabadosInfo & { zoneName?: string; zoneDisplayName?: string }) | null
  setSelectedTexture: SetState<
    (BackAcabadosInfo & { zoneName?: string; zoneDisplayName?: string }) | null
  >
  // pasteIcons: PasteRecord | null
  setPasteIcons: SetState<PasteRecord>
  search: string
  setSearch: SetState<string>
  IamUp: boolean
  setIamUp: SetState<boolean>
  modeEditor: string
  setModeEditor: SetState<string>
  pendingGLBChanges: Map<string, string>
  setPendingGLBChanges: SetState<Map<string, string>>
  handleLinkedEncimeraZone: (linkedTextureID: number) => Promise<boolean>
}
const LinkedItemZoneContext = createContext<LinkedContextState>(null)
type Props = {
  children: ReactNode
}

export function LinkedItemZoneProvider({ children }: Props) {
  const currId = useRef('')
  const object = useRef<SceneObjects | undefined>(undefined)
  const [copiedIconId, setCopiedIconId] = useState('') // Id del icono actual copiado
  const [isModel, setIsModel] = useState(false)
  const [highlighted, setHighlighted] = useState('') // Lo que se seleccione, arriba, o en los hijos, o el modelo, sustituye currentZone
  const [modelo, setModelo] = useState('')
  const [acabado, setAcabado] = useState('')
  const [centerInfo, setCenterInfo] = useState<CenterInfo>([])
  const [rightInfo, setRightInfo] = useState<RightInfo>({ name: '', textures: [] })
  const [copiedTexture, setCopiedTexture] = useState<Textures[number] | null>(null)
  const [linkedOptions, setLinkedOptions] = useState<Options | null>(null)
  const childrenObject = useRef<LinkedItemSon>(null)
  // const [pasteIcons, setPasteIcons] = useState<PasteRecord | null>(null)
  const [selectedTexture, setSelectedTexture] = useState<
    (BackAcabadosInfo & { zoneName?: string; zoneDisplayName?: string }) | null
  >(null)
  const [search, setSearch] = useState('')
  const [IamUp, setIamUp] = useState(false)

  const [modeEditor, setModeEditor] = useState('All')
  const [pendingGLBChanges, setPendingGLBChanges] = useState<Map<string, string>>(new Map())
  const lastInitializedElement = useRef<string | null>(null)
  const highlightedRef = useRef<string>('')
  const lastLinkedOptionsKeyRef = useRef<string | null>(null)
  const linkedOptionsFetchInFlightRef = useRef(false)
  const linkedOptionsErrorCooldownRef = useRef<Map<string, number>>(new Map())

  const state = {
    currId,
    object,
    copiedIconId,
    setCopiedIconId,
    isModel,
    setIsModel,
    highlighted,
    setHighlighted,
    modelo,
    setModelo,
    acabado,
    setAcabado,
    centerInfo,
    setCenterInfo,
    rightInfo,
    setRightInfo,
    copiedTexture,
    setCopiedTexture,
    linkedOptions,
    childrenObject,
    pasteIcons: null,
    setPasteIcons: () => {},
    selectedTexture,
    setSelectedTexture,
    search,
    setSearch,
    IamUp,
    setIamUp,
    modeEditor,
    setModeEditor,
    pendingGLBChanges,
    setPendingGLBChanges,
    handleLinkedEncimeraZone,
  }

  useLayoutEffect(() => {
    const unsubscribe = useAppStore.subscribe((s) => {
      const coid = s.currentObjectID

      if (currId.current !== coid) {
        currId.current = coid
        // Preservar highlighted si es un linkedSon para evitar que se resetee cuando cambia el armazón
        const wasLinkedSon = highlightedRef.current.startsWith('linkedSon_')
        setRightInfo({ name: '', textures: [] })
        setCenterInfo([])
        // Solo resetear highlighted si NO era un linkedSon
        // Esto preserva el estado cuando estás trabajando con un hijo
        if (!wasLinkedSon) {
          setHighlighted('')
        }
        setAcabado('')
        setModelo('')
        setSelectedTexture(null)
        lastInitializedElement.current = null

        if (coid.startsWith('linkedItem_')) {
          const item = s.items.get(coid)
          if (item && item.type === 'linkedItem' && (item as any).idUnidades) {
            const linkedOptionsKey = `${coid}|${(item as any).idUnidades}`
            const now = Date.now()
            const cooldownUntil = linkedOptionsErrorCooldownRef.current.get(linkedOptionsKey) || 0
            if (cooldownUntil > now) {
              return
            }
            if (linkedOptionsFetchInFlightRef.current && lastLinkedOptionsKeyRef.current === linkedOptionsKey) {
              return
            }
            if (lastLinkedOptionsKeyRef.current === linkedOptionsKey && linkedOptions) {
              return
            }
            linkedOptionsFetchInFlightRef.current = true
            lastLinkedOptionsKeyRef.current = linkedOptionsKey
            fetch(
              getProxyUrl(
                `api/acabados/getelementosenlazadosunidadacabados/${(item as any).idUnidades}`,
              ),
              {
                method: 'GET',
                mode: 'cors',
                headers: {
                  'Accept-Language': 'ES',
                  'Content-Type': 'application/json',
                  Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
                },
              },
            )
              .then((info) => info.json())
              .then((info2) => {
                setLinkedOptions(info2)
                linkedOptionsFetchInFlightRef.current = false
              })
              .catch((error) => {
                // Evitar bucle de errores en consola y requests repetidos inmediatos.
                linkedOptionsErrorCooldownRef.current.set(linkedOptionsKey, Date.now() + 3000)
                linkedOptionsFetchInFlightRef.current = false
                setLinkedOptions(null)
              })
          }
        }
      }
    })

    const handleLinkedOptionsUpdate = (event: CustomEvent) => {
      const { linkedID, newLinkedOptions } = event.detail

      if (linkedID === currId.current) {
        setLinkedOptions(newLinkedOptions as any)
      }
    }

    window.addEventListener('linkedOptionsUpdated', handleLinkedOptionsUpdate as EventListener)

    const unsubscribe2 = useAppStore.subscribe((s) => {
      const coid = s.currentObjectID
      const newObject = s.items.get(coid)
      const isDragging3D = s.mode === 'MODE_DRAGGING_ITEM_3D'

      object.current = newObject

      // Durante drag evitar recalcular/rehidratar zonas y texturas de linked,
      // que puede generar parpadeos negros y carga innecesaria.
      if (isDragging3D) {
        return
      }

      if (object.current && (coid.startsWith('linkedItem_') || coid.startsWith('linkedSon_'))) {
        const isLinkedItem = coid.startsWith('linkedItem_')
        const isLinkedSon = coid.startsWith('linkedSon_')
        
        // Si es un linkedSon, no ejecutar lógica de inicialización de zonas
        // Los linkedSon se manejan en otro lugar y no tienen zonas
        if (isLinkedSon) {
          return
        }
        
        // Si estamos viendo un hijo (highlighted es linkedSon), NO inicializar zonas del padre
        const isViewingLinkedSon = highlightedRef.current.startsWith('linkedSon_')
        
        // Solo saltar si ya está inicializado Y el catalogID no ha cambiado
        // Solo verificar _armazonUpdateCounter si es un linkedItem
        const lastCatalogID = lastInitializedElement.current?.split('|')[1] || ''
        const currentCatalogID = String(object.current.catalogID || '')
        const armazonUpdateCounter = (object.current as any)._armazonUpdateCounter || 0
        const lastArmazonCounter = lastInitializedElement.current?.split('|')[2] || '0'

        const shouldSkipInitialization =
          lastInitializedElement.current &&
          lastInitializedElement.current.startsWith(coid) &&
          lastCatalogID === currentCatalogID &&
          lastArmazonCounter === String(armazonUpdateCounter)

        // Si el contador cambió (armazón se actualizó), SIEMPRE actualizar selectedTexture si es del armazón
        // Esto funciona tanto en LinkedObjectZones como en linkedSonZones
        if (!shouldSkipInitialization && object.current.textures) {
          const armazonTexture = object.current.textures.find(
            (t: any) => t.zoneDisplayName === 'Armazon'
          )
          // Actualizar selectedTexture si la zona actual es Armazon o si estamos viendo un linkedSon
          if (armazonTexture) {
            const currentSelectedZoneDisplayName = selectedTexture?.zoneDisplayName
            const highlightedZoneName = highlightedRef.current?.split('!')?.[1]
            const highlightedTexture = highlightedZoneName
              ? object.current.textures.find((t: any) => t.zoneName === highlightedZoneName)
              : null
            const isArmazonSelected = 
              currentSelectedZoneDisplayName === 'Armazon' ||
              highlightedTexture?.zoneDisplayName === 'Armazon' ||
              highlightedTexture?.zoneName === armazonTexture.zoneName
            
            // SIEMPRE actualizar selectedTexture cuando cambia el armazón
            // Esto asegura que el acabado se actualice independientemente de la vista
            setSelectedTexture(armazonTexture as any)
          }
        }

        // Si estamos viendo un linkedSon, NO ejecutar handleZoneClick que cambiaría la vista
        if (isViewingLinkedSon) {
          // Actualizar el tracking pero NO ejecutar handleZoneClick
          if (!shouldSkipInitialization) {
            lastInitializedElement.current = coid + '|' + currentCatalogID + '|' + armazonUpdateCounter
          }
          return
        }

        if (shouldSkipInitialization) {
          return
        }

        // Actualizar el tracking con el nuevo catalogID y contador de armazón
        lastInitializedElement.current = coid + '|' + currentCatalogID + '|' + armazonUpdateCounter

        // Solo inicializar zonas si NO hay una zona ya seleccionada (highlighted está vacío)
        // Si ya hay una zona seleccionada, solo actualizar selectedTexture pero no cambiar la vista
        const hasHighlightedZone = highlightedRef.current && !highlightedRef.current.startsWith('linkedSon_')
        
        if (!hasHighlightedZone && 
          object.current.textures &&
          Array.isArray(object.current.textures) &&
          object.current.textures.length > 0
        ) {
          const validTextures = object.current.textures.filter(
            (texture) => texture.modelo !== null && texture.acabado !== null,
          )

          const firstValidTexture =
            validTextures.find((texture) => texture.zoneDisplayName !== 'Armazon') ||
            validTextures[0] // Fallback a la primera si no hay otras opciones

          if (firstValidTexture) {
            const newHighlighted = handleConcatStrings(
              object.current.id,
              firstValidTexture.zoneName,
            )
            setHighlighted(newHighlighted)

            setTimeout(async () => {
              await handleZoneClick(state, firstValidTexture)
            }, 0)
            lastInitializedElement.current = coid + '|' + String(object.current.catalogID || '')
          } else {
            console.warn('No se encontró ninguna textura válida con modelo y acabado no nulos.')
          }
        }

        // const inicializado = Object.fromEntries(
        //   (object.current.textures as any[]).map((el) => [el.zone, 'cantPaste']),
        // )
        // setPasteIcons(inicializado)
      }
    })

    return () => {
      unsubscribe()
      unsubscribe2()
      // Remover listener del evento personalizado
      window.removeEventListener('linkedOptionsUpdated', handleLinkedOptionsUpdate as EventListener)
    }
  }, [])

  // Mantener highlightedRef sincronizado con highlighted
  useEffect(() => {
    highlightedRef.current = highlighted
  }, [highlighted])

  useEffect(() => {
    const unsubscribe = useAppStore.subscribe(
      (s) => (childrenObject.current = s.items.get(highlighted.split('!')?.at(0)) || null),
    )
    return () => unsubscribe()
  }, [highlighted])

  useEffect(() => {
    if (highlighted && highlighted.startsWith('linkedSon_')) {
      const childItem = useAppStore.getState().items.get(highlighted as AnyID)

      if (childItem && childItem.description === 'Encimera') {
        const linkedTextureID = childItem.textures?.[0]?.linkedTextureID

        if (linkedTextureID) {
          handleLinkedEncimeraZone(linkedTextureID).then((ok) => {
            if (ok && childItem.textures && childItem.textures.length > 0) {
              const childTexture = childItem.textures[0]

              setSelectedTexture({
                id: childTexture.id,
                searchID: childTexture.searchID,
                zoneName: 'Encimera',
                zoneDisplayName: 'Encimera',
                name: childTexture.name,
                description: childTexture.description,
                url: childTexture.url,
                acabado: childTexture.acabado,
                groupID: childTexture.groupID,
                idAcabadoUnidadModeloAcabado: childTexture.idAcabadoUnidadModeloAcabado,
                linkedTextureID: childTexture.linkedTextureID,
                modelo: childTexture.modelo,
              })
            }
          })
        }
      }
    }
  }, [highlighted])

  useEffect(() => {
    const handleTextureInherited = (event: CustomEvent) => {
      const { texture, elementId } = event.detail

      if (highlighted === elementId || highlighted.startsWith('linkedSon_')) {
        if (rightInfo.textures && rightInfo.textures.length > 0) {
          const matchingTexture = rightInfo.textures.find(
            (t: any) =>
              t.nombre === texture.name ||
              t.descripcion === texture.name ||
              t.nombre === texture.description ||
              t.descripcion === texture.description,
          )

          if (matchingTexture) {
            setSelectedTexture({
              ...matchingTexture,
              zoneName: 'Encimera',
              zoneDisplayName: 'Encimera',
            })
          }
        }
      }
    }

    window.addEventListener('textureInherited', handleTextureInherited as EventListener)

    return () => {
      window.removeEventListener('textureInherited', handleTextureInherited as EventListener)
    }
  }, [highlighted, rightInfo.textures])

  async function handleLinkedEncimeraZone(linkedTextureID: number) {
    try {
      const acabadoInfo = await fetchAcabadoZonaModeloAcabadoInfo(linkedTextureID)
      if (
        Array.isArray(acabadoInfo) &&
        acabadoInfo.length > 0 &&
        acabadoInfo[0].acabados &&
        acabadoInfo[0].acabados.length > 0
      ) {
        // Orden específico de acabados
        const acabadoOrder = [
          'MATERIA',
          'TMATT',
          'CRYSTAL',
          'STRATI',
          'MATT',
          'NATURA',
          'ECOVINTAGE',
          'BLANCOS',
          'MADERAS',
          'BASICOS',
          'LINEAL',
          'RUSTIK',
          'RUSTIK2',
          'LARA',
        ]

        const centerInfo = acabadoInfo
          .flatMap((zona) =>
            zona.acabados.map((acabado) => ({
              zona: zona.zona,
              modelo: zona.modelo,
              ...acabado,
            })),
          )
          .sort((a, b) => {
            const indexA = acabadoOrder.findIndex((order) => order.trim() === a.acabado?.trim())
            const indexB = acabadoOrder.findIndex((order) => order.trim() === b.acabado?.trim())

            // Si ambos están en el orden, ordenar por índice
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB
            }
            // Si solo A está en el orden, A va primero
            if (indexA !== -1) return -1
            // Si solo B está en el orden, B va primero
            if (indexB !== -1) return 1
            // Si ninguno está en el orden, mantener orden original
            return 0
          })

        const idAcabadoUnidadModeloAcabado = centerInfo[0].idAcabadoUnidadModeloAcabado

        const texturasRaw = await fetchTexturas(idAcabadoUnidadModeloAcabado)
        const texturas = texturasRaw.map((t) => ({
          ...t,
          url: t.url,
          urlMiniatura: t.urlMiniatura ?? t.miniatura ?? null,
          nombre: t.nombre ?? t.descripcion ?? '',
          descripcion: t.descripcion ?? '',
        }))
        setRightInfo({
          name: centerInfo[0].zona || 'Encimera',
          textures: texturas,
        })
        setCenterInfo(centerInfo)

        if (centerInfo.length > 0) {
          if (highlighted && highlighted.startsWith('linkedSon_')) {
            const childItem = useAppStore.getState().items.get(highlighted as AnyID)
            if (childItem && childItem.description === 'Encimera') {
              const childAcabado = childItem.textures?.[0]?.acabado

              const matchingGroup = centerInfo.find((c) => c.acabado === childAcabado)
              if (matchingGroup) {
                setAcabado(matchingGroup.acabado)
              } else {
                setAcabado(centerInfo[0].acabado)
              }
            } else {
              const acabadoBasico = centerInfo.find(
                (c) =>
                  c.acabado?.toLowerCase().includes('básico') ||
                  c.acabado?.toLowerCase().includes('basico'),
              )
              const acabadoADefinir = acabadoBasico || centerInfo[0]
              setAcabado(acabadoADefinir.acabado)
            }
          } else {
            const acabadoBasico = centerInfo.find(
              (c) =>
                c.acabado?.toLowerCase().includes('básico') ||
                c.acabado?.toLowerCase().includes('basico'),
            )
            const acabadoADefinir = acabadoBasico || centerInfo[0]
            setAcabado(acabadoADefinir.acabado)
          }
        }

        return true
      } else {
        setRightInfo({ name: '', textures: [] })
        setCenterInfo([])
        return false
      }
    } catch (error) {
      setRightInfo({ name: '', textures: [] })
      setCenterInfo([])
      return false
    }
  }

  return <LinkedItemZoneContext.Provider value={state}>{children}</LinkedItemZoneContext.Provider>
}

export function useLinkedContext() {
  const context = useContext(LinkedItemZoneContext)
  const editLinkedTexture = useAppStore.use.editLinkedTexture()

  // Fallback seguro cuando no existe proveedor del contexto
  if (!context) {
    const noop = () => {}
    const noopAsync = async () => {}
    return {
      linkedOptions: null,
      acabado: '',
      actions: {
        handlePaste: noopAsync,
        handlePasteAll: noopAsync,
        updateRightInfo: noop,
        handleZoneClick: noop as any,
        handleTextureClick: noop as any,
        handleGroupClick: noop as any,
        handleApplyToAllItems: noop,
        handleCopy: noop as any,
      },
    } as any
  }

  const linkedOptions = context.linkedOptions
  const actions = {
    handlePaste: (
      e: ReactMouseEvent,
      zoneInfo: { zoneName: string; zone: string },
      canPaste: string,
    ): Promise<void> => handlePaste({ e, zoneInfo, ctx: context, canPaste, editLinkedTexture }),
    handlePasteAll: () => HandlePasteAll(context, editLinkedTexture),
    updateRightInfo: (name: string, groupID: number) => updateRightInfo(name, groupID, context),
    handleZoneClick: (linkedTextureId: Textures[number]) =>
      handleZoneClick(context, linkedTextureId),
    handleTextureClick: (texture: BackAcabadosInfo) => handleTextureClick(texture, context),
    handleGroupClick: (id: number, acabado: string) => handleGroupClick(id, context, acabado),
    handleApplyToAllItems: () => handleApplyToAllItems(),
    handleCopy: (property: Textures[number], e: ReactMouseEvent) =>
      handleCopy(context, property, e),
    // resetCopyAndPaste: () => {
    //   context.setCopiedIconId('')
    //   context.setPasteIcons((prev) => {
    //     const newIcons = Object.fromEntries(
    //       Object.entries(prev).map(([key, _]) => [key, 'cantPaste']),
    //     )
    //     return newIcons
    //   })
    // }
    // ,
  }

  return { ...context, linkedOptions, actions }
}

export type LinkedContext = ReturnType<typeof useLinkedContext>
